import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { createSyncedRows, type RemoteSettings } from '../services/sync/syncedRows'
import { useCorpusStore } from './corpus'
import { useSettingsStore } from './settings'
import { playChime } from '../utils/chime'

// Writing sprints: a timed burst in the editor that tallies the same typed
// words the writing log counts. The sprint is a lens on that writing, not a
// second record of it; finished sprints are kept (and synced) for history.

export interface SprintRecord {
  id: string
  /** Milliseconds since the epoch. */
  startedAt: number
  plannedMinutes: number
  /** How long it actually ran; shorter when ended early. */
  durationMs: number
  added: number
  removed: number
  /** Pages written in during the sprint. */
  pages: number
}

interface ActiveSprint {
  id: string
  startedAt: number
  plannedMinutes: number
  added: number
  removed: number
  pageIds: string[]
  /** When it was paused, while paused (Ctrl+Alt+Shift+P). */
  pausedAt?: number | null
  /** Time spent paused before now; the clock doesn't count it. */
  pausedMs?: number
}

const ACTIVE_KEY = 'ink-sprint-active'

function parseRecord(raw: any): SprintRecord | null {
  const count = (v: unknown) => typeof v === 'number' && Number.isFinite(v) && v >= 0
  if (!raw || typeof raw.id !== 'string' || !count(raw.startedAt) || !count(raw.durationMs)
    || !count(raw.added) || !count(raw.removed)) return null
  return {
    id: raw.id,
    startedAt: raw.startedAt,
    plannedMinutes: count(raw.plannedMinutes) ? raw.plannedMinutes : Math.round(raw.durationMs / 60_000),
    durationMs: raw.durationMs,
    added: raw.added,
    removed: raw.removed,
    pages: count(raw.pages) ? raw.pages : 0,
  }
}

function loadActive(): ActiveSprint | null {
  try {
    const raw = JSON.parse(localStorage.getItem(ACTIVE_KEY) || 'null')
    return raw && typeof raw.id === 'string' && Array.isArray(raw.pageIds) ? raw : null
  } catch {
    return null
  }
}

/** Words per minute of words added, the pace of the typing itself. */
export function wordsPerMinute(record: Pick<SprintRecord, 'added' | 'durationMs'>): number {
  const minutes = record.durationMs / 60_000
  return minutes > 0 ? Math.round(record.added / minutes) : 0
}

export const useSprintStore = defineStore('sprints', () => {
  const corpus = useCorpusStore()
  const settings = useSettingsStore()
  /** How sprints run (Settings > Goals & Habits). */
  const prefs = () => settings.goalPrefs.sprint
  const history = createSyncedRows<SprintRecord>({
    storageKey: 'ink-sprints',
    remotePrefix: 'sprint.',
    parse: parseRecord,
  })

  const active = ref<ActiveSprint | null>(loadActive())
  /** The sprint just finished, shown as a summary until dismissed. */
  const summary = ref<(SprintRecord & { endedEarly: boolean }) | null>(null)
  const preferredMinutes = computed(() => prefs().defaultMinutes)

  // A clock that ticks only while a sprint runs.
  const now = ref(Date.now())
  let ticker: ReturnType<typeof setInterval> | null = null

  /** Time paused so far, including a pause still going on. */
  function pausedTotal(at: number): number {
    const sprint = active.value
    if (!sprint) return 0
    return (sprint.pausedMs ?? 0) + (sprint.pausedAt ? Math.max(0, at - sprint.pausedAt) : 0)
  }
  const paused = computed(() => !!active.value?.pausedAt)
  // A pause moves the end later by as long as it lasts.
  const endsAt = computed(() => (active.value
    ? active.value.startedAt + active.value.plannedMinutes * 60_000 + pausedTotal(now.value)
    : 0))
  const remainingMs = computed(() => Math.max(0, endsAt.value - now.value))
  /** Time run so far, not counting pauses. */
  const elapsedMs = computed(() => (active.value ? Math.max(0, now.value - active.value.startedAt - pausedTotal(now.value)) : 0))
  /** Time past the planned end, when the writer lets a sprint run over. */
  const overtimeMs = computed(() => (active.value ? Math.max(0, now.value - endsAt.value) : 0))
  const progress = computed(() =>
    active.value ? 1 - remainingMs.value / (active.value.plannedMinutes * 60_000) : 0)

  const records = computed(() =>
    Object.values(history.values.value).sort((a, b) => b.startedAt - a.startedAt))

  function persistActive() {
    if (active.value) localStorage.setItem(ACTIVE_KEY, JSON.stringify(active.value))
    else localStorage.removeItem(ACTIVE_KEY)
  }

  // Chimes ring once each per sprint, as the clock crosses a minute left and the end.
  let warned = false
  let rang = false

  function tick() {
    now.value = Date.now()
    if (!active.value || active.value.pausedAt) return
    const { warn, chime, overtime } = prefs()
    if (warn && !warned && active.value.plannedMinutes > 1 && endsAt.value - now.value <= 60_000) {
      warned = true
      playChime('warn')
    }
    if (now.value < endsAt.value) return
    if (chime && !rang) {
      rang = true
      playChime('end')
    }
    if (!overtime) finish(false)
  }

  function startTicking() {
    if (ticker) return
    tick()
    ticker = setInterval(tick, 1_000)
  }

  function stopTicking() {
    if (ticker) clearInterval(ticker)
    ticker = null
  }

  function start(minutes: number) {
    if (active.value) return
    summary.value = null
    // A sprint that has already reached its end once (before a reload) stays quiet.
    warned = false
    rang = false
    active.value = {
      id: crypto.randomUUID(),
      startedAt: Date.now(),
      plannedMinutes: minutes,
      added: 0,
      removed: 0,
      pageIds: [],
    }
    persistActive()
    startTicking()
  }

  /** Tallies a credited change to a page, as the writing session reports it. */
  function record(pageId: string, wordDelta: number) {
    const sprint = active.value
    if (!sprint || corpus.findNode(pageId)?.includeInWordCount === false) return
    if (wordDelta > 0) sprint.added += wordDelta
    else sprint.removed -= wordDelta
    if (!sprint.pageIds.includes(pageId)) sprint.pageIds.push(pageId)
    persistActive()
  }

  function finish(endedEarly = Date.now() < endsAt.value) {
    const sprint = active.value
    if (!sprint) return
    // Overtime counts when the writer lets sprints run over; otherwise the planned length is the most.
    const pausedFor = pausedTotal(Date.now())
    const end = prefs().overtime ? Date.now() : Math.min(Date.now(), sprint.startedAt + sprint.plannedMinutes * 60_000 + pausedFor)
    const durationMs = Math.max(0, end - sprint.startedAt - pausedFor)
    const result: SprintRecord = {
      id: sprint.id,
      startedAt: sprint.startedAt,
      plannedMinutes: sprint.plannedMinutes,
      durationMs,
      added: sprint.added,
      removed: sprint.removed,
      pages: sprint.pageIds.length,
    }
    active.value = null
    persistActive()
    stopTicking()
    if (durationMs >= prefs().minSeconds * 1_000) history.write(result.id, result)
    if (prefs().summary) summary.value = { ...result, endedEarly }
  }

  function dismissSummary() {
    summary.value = null
  }

  /** Pauses the running sprint, or carries on with a paused one. */
  function togglePause() {
    const sprint = active.value
    if (!sprint) return
    const at = Date.now()
    if (sprint.pausedAt) {
      sprint.pausedMs = (sprint.pausedMs ?? 0) + Math.max(0, at - sprint.pausedAt)
      sprint.pausedAt = null
    } else {
      sprint.pausedAt = at
    }
    now.value = at
    persistActive()
  }

  /** Starts a sprint of the writer's usual length, or ends the one running. */
  function toggle() {
    if (active.value) finish()
    else start(prefs().defaultMinutes)
  }

  // A sprint in progress survives a reload; one that ran out meanwhile ends
  // now, unless sprints may run over. Its chimes have rung if it is past them.
  if (active.value) {
    warned = rang = Date.now() >= endsAt.value
    startTicking()
  }

  async function syncWithRemote(remote: RemoteSettings) {
    await history.syncWithRemote(remote)
  }

  return {
    active,
    paused,
    togglePause,
    summary,
    preferredMinutes,
    remainingMs,
    elapsedMs,
    overtimeMs,
    progress,
    records,
    start,
    record,
    finish,
    toggle,
    dismissSummary,
    syncWithRemote,
  }
})
